import React, { useState, useMemo } from 'react';
import { Hash, X, Search, Check } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

/**
 * MultiSelectTopics — searchable dropdown for selecting multiple topics.
 *
 * Props:
 *   topics: [{ name: string, count?: number }]
 *   selected: string[]  -- currently selected topic names
 *   onChange: (next: string[]) => void
 *   max: number         -- maximum number of selectable topics (default 5)
 *   placeholder: string
 *   testId: string
 */
export default function MultiSelectTopics({
  topics = [],
  selected = [],
  onChange,
  max = 5,
  placeholder = 'Select topics…',
  testId = 'multi-select-topics',
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return topics;
    return topics.filter(t => t.name.toLowerCase().includes(q));
  }, [topics, search]);

  const toggle = (name) => {
    const isSelected = selected.includes(name);
    if (isSelected) {
      onChange(selected.filter(x => x !== name));
    } else if (selected.length < max) {
      onChange([...selected, name]);
    }
  };

  const remove = (name, e) => {
    e?.stopPropagation();
    onChange(selected.filter(x => x !== name));
  };

  return (
    <div data-testid={testId} className="space-y-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            data-testid={`${testId}-trigger`}
            className="w-full min-h-[42px] flex items-center justify-between gap-2 px-3 py-2 rounded-md border border-slate-200 bg-white text-sm text-slate-600 hover:border-brand-navy/40 focus:outline-none focus:ring-2 focus:ring-brand-navy/20 transition-colors"
          >
            <div className="flex items-center gap-1.5 flex-wrap flex-1 min-h-[24px]">
              {selected.length === 0 ? (
                <span className="text-slate-400">{placeholder}</span>
              ) : (
                selected.map(name => (
                  <span key={name}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-brand-navy/10 text-brand-navy">
                    <Hash className="h-2.5 w-2.5" />
                    {name}
                    <button type="button" onClick={(e) => remove(name, e)}
                      className="hover:bg-brand-navy/20 rounded-full p-0.5 -mr-1"
                      aria-label={`Remove ${name}`}
                      data-testid={`${testId}-remove-${name.replace(/\s+/g, '-')}`}>
                      <X className="h-2.5 w-2.5" />
                    </button>
                  </span>
                ))
              )}
            </div>
            <span className="text-[11px] text-slate-400 whitespace-nowrap">{selected.length}/{max}</span>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
          <div className="px-2 pt-2">
            <div className="relative">
              <Search className="absolute left-2 top-2.5 h-3.5 w-3.5 text-slate-400" />
              <input
                autoFocus
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search topics..."
                className="w-full pl-7 pr-2 py-2 text-sm border border-slate-200 rounded-md outline-none focus:border-brand-navy/40"
                data-testid={`${testId}-search`}
              />
            </div>
          </div>
          <div className="max-h-72 overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <p className="text-xs text-slate-400 text-center py-4">No matching topics.</p>
            ) : (
              filtered.map(t => {
                const isSelected = selected.includes(t.name);
                const atMax = !isSelected && selected.length >= max;
                return (
                  <button
                    type="button"
                    key={t.name}
                    onClick={() => toggle(t.name)}
                    disabled={atMax}
                    data-testid={`${testId}-option-${t.name.replace(/\s+/g, '-')}`}
                    className={`w-full flex items-center justify-between px-3 py-1.5 text-sm text-left transition-colors ${
                      atMax
                        ? 'text-slate-300 cursor-not-allowed'
                        : 'text-slate-700 hover:bg-slate-50'
                    }`}>
                    <span className="flex items-center gap-1.5 truncate">
                      <Hash className="h-3 w-3 opacity-50" />
                      {t.name}
                    </span>
                    <span className="flex items-center gap-2 flex-shrink-0">
                      {t.count !== undefined && t.count > 0 && (
                        <span className="text-xs text-slate-400">{t.count}</span>
                      )}
                      {isSelected && <Check className="h-3.5 w-3.5 text-brand-navy" />}
                    </span>
                  </button>
                );
              })
            )}
          </div>
          {selected.length > 0 && (
            <div className="border-t border-slate-100 px-2 py-1.5 flex items-center justify-between">
              <button
                type="button"
                onClick={() => onChange([])}
                className="text-xs text-slate-500 hover:text-rose-500 px-2 py-1"
                data-testid={`${testId}-clear-all`}>
                Clear all
              </button>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="text-xs font-medium text-brand-navy hover:text-brand-dark px-2 py-1">
                Done
              </button>
            </div>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
}
